import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
  Autocomplete
} from '@mui/material';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';

const ManualAccountingEntry = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = tabParam === 'payment' ? 1 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);
  const showTabs = !tabParam; // Hide tabs if specific tab is requested via URL
  const [ledgers, setLedgers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    ledgerId: '',
    amount: '',
    description: '',
    paymentMethod: 'cash',
    bankAccountId: ''
  });

  // Fetch all ledgers
  useEffect(() => {
    fetchLedgers();
  }, []);

  // Fetch bank accounts when payment method is bank
  useEffect(() => {
    if (formData.paymentMethod === 'bank') {
      fetchBankAccounts();
    }
  }, [formData.paymentMethod]);

  const fetchLedgers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/ledgers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch ledgers');
      }

      const data = await response.json();
      const ledgersData = data.data || data || [];
      // Exclude customer ledgers - only show Supplier and General ledgers
      const nonCustomerLedgers = ledgersData.filter(ledger => ledger.type !== 'Customer');
      setLedgers(nonCustomerLedgers);
    } catch (error) {
      console.error('Error fetching ledgers:', error);
      setSnackbar({
        open: true,
        message: 'Error loading ledgers',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/ledgers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bank accounts');
      }

      const data = await response.json();
      const ledgersData = data.data || data || [];
      
      // Filter bank accounts (Group Code 801-900) and Cash
      const banks = ledgersData.filter(ledger => {
        const groupCode = parseInt(ledger.groupCode);
        return ledger.code === 'CASH' || (groupCode >= 801 && groupCode <= 900);
      });
      
      setBankAccounts(banks);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Update URL query param only if tabs are visible
    if (showTabs) {
      setSearchParams({ tab: newValue === 0 ? 'receipt' : 'payment' });
    }
    // Reset form when switching tabs
    setFormData({
      date: new Date().toISOString().split('T')[0],
      ledgerId: '',
      amount: '',
      description: '',
      paymentMethod: 'cash',
      bankAccountId: ''
    });
    setSelectedLedger(null);
    setSelectedBankAccount(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.ledgerId || !formData.amount || !formData.date) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error'
      });
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      setSnackbar({
        open: true,
        message: 'Amount must be greater than zero',
        severity: 'error'
      });
      return;
    }

    if (formData.paymentMethod === 'bank' && !formData.bankAccountId) {
      setSnackbar({
        open: true,
        message: 'Please select a bank account',
        severity: 'error'
      });
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const endpoint = activeTab === 0 
        ? `${baseURL}/accounting-entries/manual/receipt`
        : `${baseURL}/accounting-entries/manual/payment`;

      const payload = {
        ledgerId: formData.ledgerId,
        amount: parseFloat(formData.amount),
        date: formData.date,
        description: formData.description,
        paymentMethod: formData.paymentMethod,
        ...(formData.paymentMethod === 'bank' && { bankAccountId: formData.bankAccountId })
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create entry');
      }

      setSnackbar({
        open: true,
        message: data.message || 'Entry created successfully',
        severity: 'success'
      });

      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        ledgerId: '',
        amount: '',
        description: '',
        paymentMethod: 'cash',
        bankAccountId: ''
      });
      setSelectedLedger(null);
      setSelectedBankAccount(null);
    } catch (error) {
      console.error('Error creating entry:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Error creating entry',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <GirviPageShell
        title="Manual Entry"
        subtitle="Accounting · Vouchers"
        chipLabel="Accounting"
        maxWidth="xl"
      >
        <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" gutterBottom>
            {tabParam === 'receipt' ? 'Create Receipt' : tabParam === 'payment' ? 'Create Payment' : 'Manual Accounting Entry'}
          </Typography>

          {showTabs && (
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Receipt (Money Coming In)" />
                <Tab label="Payment (Money Going Out)" />
              </Tabs>
            </Box>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  required
                  options={ledgers}
                  getOptionLabel={(option) => 
                    `${option.firstName} ${option.lastName}${option.groupCode ? ` (Group: ${option.groupCode})` : ''}`
                  }
                  value={selectedLedger}
                  onChange={(event, newValue) => {
                    setSelectedLedger(newValue);
                    setFormData(prev => ({
                      ...prev,
                      ledgerId: newValue ? newValue._id : ''
                    }));
                  }}
                  filterOptions={(options, { inputValue }) => {
                    return options.filter(option => {
                      const searchTerm = inputValue.toLowerCase();
                      const fullName = `${option.firstName} ${option.lastName}`.toLowerCase();
                      const code = option.code ? option.code.toLowerCase() : '';
                      const groupCode = option.groupCode ? option.groupCode.toString() : '';
                      return fullName.includes(searchTerm) || 
                             code.includes(searchTerm) || 
                             groupCode.includes(searchTerm);
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ledger *"
                      variant="outlined"
                      placeholder="Search by name, code, or group code..."
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option._id}>
                      <Box>
                        <Typography variant="body1">
                          {option.firstName} {option.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.code && `Code: ${option.code}`}
                          {option.groupCode && ` | Group: ${option.groupCode}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  noOptionsText="No ledgers found"
                  loading={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Amount (₹)"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Payment Method</FormLabel>
                  <RadioGroup
                    row
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                  >
                    <FormControlLabel value="cash" control={<Radio />} label="Cash" />
                    <FormControlLabel value="bank" control={<Radio />} label="Bank" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              {formData.paymentMethod === 'bank' && (
                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    required
                    options={bankAccounts}
                    getOptionLabel={(option) => 
                      `${option.firstName} ${option.lastName}${option.code ? ` (${option.code})` : ''}`
                    }
                    value={selectedBankAccount}
                    onChange={(event, newValue) => {
                      setSelectedBankAccount(newValue);
                      setFormData(prev => ({
                        ...prev,
                        bankAccountId: newValue ? newValue._id : ''
                      }));
                    }}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter(option => {
                        const searchTerm = inputValue.toLowerCase();
                        const fullName = `${option.firstName} ${option.lastName}`.toLowerCase();
                        const code = option.code ? option.code.toLowerCase() : '';
                        return fullName.includes(searchTerm) || 
                               code.includes(searchTerm);
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bank Account *"
                        variant="outlined"
                        placeholder="Search by name or code..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} key={option._id}>
                        <Box>
                          <Typography variant="body1">
                            {option.firstName} {option.lastName}
                          </Typography>
                          {option.code && (
                            <Typography variant="caption" color="text.secondary">
                              Code: {option.code}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    noOptionsText="No bank accounts found"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter description for this entry..."
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitting || loading}
                  sx={{ mt: 2 }}
                >
                  {submitting ? (
                    <>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      Creating...
                    </>
                  ) : (
                    tabParam === 'receipt' ? 'Create Receipt Entry' : tabParam === 'payment' ? 'Create Payment Entry' : `Create ${activeTab === 0 ? 'Receipt' : 'Payment'} Entry`
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </GirviPageShell>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ManualAccountingEntry;



